import { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import {
  GitBranch, Plus, Pencil, Trash2, Power, AlertTriangle, ArrowRight, Loader2, Shield, FlaskConical, X,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loading, ErrorView, Forbidden } from "@/components/StateViews";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDate } from "@/lib/format";

const MATCH_LABELS = {
  ad_id: "Ad ID", ad_name: "Ad Name",
  form_id: "Form ID", form_name: "Form Name",
  adset_id: "Ad Set ID", adset_name: "Ad Set Name",
  campaign_id: "Campaign ID", campaign_name: "Campaign Name",
  fallback: "Fallback (no match)",
};

// UI source type -> registry key
const SOURCE_TYPES = [
  { key: "form", label: "Form" },
  { key: "campaign", label: "Campaign" },
  { key: "adset", label: "Ad Set" },
  { key: "ad", label: "Ad" },
];

// Scopes whose match is driven only by lead source (no granular Meta picker needed).
const SCOPE_ONLY = ["meta_all", "manual", "csv", "all"];

const SCOPE_BADGE = {
  meta_all: "Meta · All Campaigns",
  meta_specific: "Meta · Specific",
  manual: "Manual",
  csv: "CSV Import",
  all: "All Sources",
};

// "adset_name" -> { source: "adset", by: "name" }
function decompose(mt) {
  if (!mt || mt === "fallback") return { source: "form", by: "id" };
  const i = mt.lastIndexOf("_");
  return { source: mt.slice(0, i), by: mt.slice(i + 1) };
}
const compose = (source, by) => `${source}_${by}`;
const sourceLabel = (s) => [s.source_name, s.source_id].filter(Boolean).join(" / ");

function RuleForm({ rule, users, options, onClose, onSaved }) {
  const isFallback = rule?.match_type === "fallback";
  // Existing rules without a scope are scope-agnostic — preserve that exactly via "all".
  const initialScope = rule ? (rule.source_scope || (isFallback ? null : "all")) : "meta_specific";
  const blank = {
    rule_name: "", status: "Active", match_type: "form_id", match_value: "", match_label: "",
    source_scope: "meta_specific", condition_groups: [],
    assignment_type: "Normal Round Robin", assigned_users: [], weights: {}, priority: 10,
  };
  const [form, setForm] = useState(rule
    ? { ...blank, ...rule, source_scope: initialScope, condition_groups: rule.condition_groups || [] }
    : blank);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const exec = users.filter((u) => u.role !== "Admin" && u.status !== "Inactive");

  const init = decompose(form.match_type);
  const [sourceType, setSourceType] = useState(init.source);
  const [matchBy, setMatchBy] = useState(init.by);
  const [sources, setSources] = useState([]);

  const scope = form.source_scope;
  const scopeOnly = SCOPE_ONLY.includes(scope);
  // Legacy granular Meta picker shows for "Specific Campaigns" rules (and legacy rules
  // that still carry a granular match_type without OR-groups).
  const showLegacyPicker = !isFallback && scope === "meta_specific";
  const groups = form.condition_groups || [];
  const scopeLabels = options?.source_scope_labels || {};
  const condFields = options?.rule_condition_fields || [];
  const condOps = options?.rule_condition_operators || [];

  useEffect(() => {
    if (isFallback || !showLegacyPicker) return undefined;
    let alive = true;
    api.get("/integrations/meta/sources", { params: { source_type: sourceType } })
      .then(({ data }) => { if (alive) setSources(data.data || []); })
      .catch(() => { if (alive) setSources([]); });
    return () => { alive = false; };
  }, [sourceType, isFallback, showLegacyPicker]);

  const finalMatchType = isFallback ? "fallback" : (showLegacyPicker ? compose(sourceType, matchBy) : "");
  const valueOptions = sources.filter((s) => (matchBy === "id" ? s.source_id : s.source_name));
  const onPickValue = (v) => {
    const hit = sources.find((s) => (matchBy === "id" ? s.source_id : s.source_name) === v);
    setForm((f) => ({ ...f, match_value: v, match_label: hit ? sourceLabel(hit) : v }));
  };

  // ----- OR condition groups -----
  const addGroup = () => set("condition_groups", [...groups, { conditions: [{ field: condFields[0] || "campaign_name", operator: "contains", value: "" }] }]);
  const removeGroup = (gi) => set("condition_groups", groups.filter((_, i) => i !== gi));
  const addCond = (gi) => set("condition_groups", groups.map((g, i) => i === gi ? { ...g, conditions: [...g.conditions, { field: condFields[0] || "campaign_name", operator: "contains", value: "" }] } : g));
  const removeCond = (gi, ci) => set("condition_groups", groups.map((g, i) => i === gi ? { ...g, conditions: g.conditions.filter((_, j) => j !== ci) } : g));
  const updCond = (gi, ci, patch) => set("condition_groups", groups.map((g, i) => i === gi ? { ...g, conditions: g.conditions.map((c, j) => j === ci ? { ...c, ...patch } : c) } : g));

  const toggleUser = (id) => {
    const has = form.assigned_users.includes(id);
    const next = has ? form.assigned_users.filter((x) => x !== id) : [...form.assigned_users, id];
    const weights = { ...form.weights };
    if (has) delete weights[id];
    set("assigned_users", next); set("weights", weights);
  };

  const weightTotal = form.assigned_users.reduce((s, id) => s + (Number(form.weights[id]) || 0), 0);
  const isWeighted = form.assignment_type === "Weighted Round Robin";

  const save = async () => {
    if (!form.rule_name.trim()) return toast.error("Rule name is required");
    if (showLegacyPicker && !form.match_value.trim() && !groups.length) return toast.error("Pick a specific Meta source value or add an OR condition group");
    if (groups.some((g) => !g.conditions?.length || g.conditions.some((c) => !["is empty", "is not empty"].includes(c.operator) && !String(c.value || "").trim()))) {
      return toast.error("Every condition needs a value (except is empty / is not empty)");
    }
    if (!form.assigned_users.length) return toast.error("Select at least one salesperson");
    if (isWeighted && weightTotal !== 100) return toast.error(`Weights must total 100% (now ${weightTotal}%)`);
    setSaving(true);
    try {
      const payload = {
        ...form, match_type: finalMatchType, source_scope: scope,
        condition_groups: groups, priority: Number(form.priority),
        weights: isWeighted ? Object.fromEntries(Object.entries(form.weights).map(([k, v]) => [k, Number(v)])) : {},
      };
      if (rule?.id) await api.put(`/assignment-rules/${rule.id}`, payload);
      else await api.post("/assignment-rules", payload);
      toast.success(rule ? "Rule updated" : "Rule created");
      onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="mb-1 block text-sm font-medium text-ink/80">Rule name</label>
        <input data-testid="rule-name-input" value={form.rule_name} onChange={(e) => set("rule_name", e.target.value)} placeholder="e.g. Clothing Campaign Routing" className="h-11 w-full rounded-xl border border-sky px-3.5 text-sm outline-none focus:border-loop focus:ring-2 focus:ring-loop/20" />
      </div>

      {!isFallback && (
        <div>
          <label className="mb-1 block text-sm font-medium text-ink/80">Apply To (Lead Source Scope)</label>
          <select data-testid="rule-source-scope" value={scope || "all"} onChange={(e) => set("source_scope", e.target.value)} className="h-11 w-full rounded-xl border border-sky bg-white px-3 text-sm outline-none focus:border-loop">
            {(options?.source_scopes || []).map((s) => <option key={s} value={s}>{scopeLabels[s] || s}</option>)}
          </select>
          <p className="mt-1 text-[11px] text-ink/45">
            {scope === "meta_all" && "Applies to every Meta lead, regardless of campaign / adset / ad / form."}
            {scope === "meta_specific" && "Applies to Meta leads matching the specific source / conditions below."}
            {scope === "manual" && "Applies to manually-created leads."}
            {scope === "csv" && "Applies to CSV-imported leads."}
            {scope === "all" && "Applies to leads from any source (scope-agnostic)."}
          </p>
        </div>
      )}

      {/* IF block — specific Meta source (legacy single condition) */}
      {showLegacyPicker && (
        <div className="rounded-2xl border-2 border-dashed border-sky bg-mist/50 p-4">
          <p className="mb-2 font-sora text-xs font-bold uppercase tracking-wide text-loop">IF the lead came from</p>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="mb-1 block text-[11px] font-medium text-ink/50">Source type</label>
                <select data-testid="rule-source-type" value={sourceType} onChange={(e) => { setSourceType(e.target.value); setForm((f) => ({ ...f, match_value: "", match_label: "" })); }} className="h-10 w-full rounded-lg border border-sky bg-white px-2 text-sm outline-none focus:border-loop">
                  {SOURCE_TYPES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-medium text-ink/50">Match by</label>
                <select data-testid="rule-match-by" value={matchBy} onChange={(e) => { setMatchBy(e.target.value); setForm((f) => ({ ...f, match_value: "", match_label: "" })); }} className="h-10 w-full rounded-lg border border-sky bg-white px-2 text-sm outline-none focus:border-loop">
                  <option value="id">ID</option>
                  <option value="name">Name</option>
                </select>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-[11px] font-medium text-ink/50">Value <span className="text-ink/35">(pick from leads already received, or type manually — optional if you use OR groups below)</span></label>
              <input list="meta-source-options" data-testid="rule-match-value" value={form.match_value} onChange={(e) => onPickValue(e.target.value)} placeholder={matchBy === "id" ? "Select or paste an ID" : "Select or type a name"} className="h-10 w-full rounded-lg border border-sky bg-white px-3 text-sm outline-none focus:border-loop" />
              <datalist id="meta-source-options">
                {valueOptions.map((s) => <option key={s.id} value={matchBy === "id" ? s.source_id : s.source_name}>{sourceLabel(s)}</option>)}
              </datalist>
              {form.match_label && form.match_label !== form.match_value && <p className="mt-1 text-[11px] text-ink/45">{form.match_label}</p>}
            </div>
          </div>
        </div>
      )}

      {/* OR condition groups */}
      {!isFallback && (
        <div className="rounded-2xl border-2 border-dashed border-sky bg-mist/50 p-4" data-testid="rule-condition-groups">
          <div className="mb-2 flex items-center justify-between">
            <p className="font-sora text-xs font-bold uppercase tracking-wide text-loop">Condition groups <span className="text-ink/40 normal-case">(optional — groups are OR, conditions inside are AND)</span></p>
            <button type="button" onClick={addGroup} data-testid="add-or-group" className="flex items-center gap-1 rounded-lg border border-sky bg-white px-2.5 py-1.5 text-xs font-semibold text-loop hover:bg-sky/30"><Plus className="h-3.5 w-3.5" />Add OR Group</button>
          </div>
          {groups.length === 0 && <p className="text-xs text-ink/45">No condition groups. {scopeOnly ? "This rule applies to the whole source scope above." : "Add granular conditions, or use the source value above."}</p>}
          <div className="space-y-3">
            {groups.map((g, gi) => (
              <div key={gi} data-testid={`or-group-${gi}`} className="rounded-xl border border-sky/70 bg-white p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="rounded-full bg-loop/10 px-2 py-0.5 text-[11px] font-bold text-loop">OR Group {gi + 1}</span>
                  <button type="button" onClick={() => removeGroup(gi)} className="rounded p-1 text-red-400 hover:bg-red-50"><X className="h-3.5 w-3.5" /></button>
                </div>
                <div className="space-y-2">
                  {g.conditions.map((c, ci) => (
                    <div key={ci} className="flex flex-wrap items-center gap-1.5">
                      {ci > 0 && <span className="text-[10px] font-bold text-ink/40">AND</span>}
                      <select data-testid={`cond-field-${gi}-${ci}`} value={c.field} onChange={(e) => updCond(gi, ci, { field: e.target.value })} className="h-9 flex-1 rounded-lg border border-sky bg-white px-2 text-xs outline-none focus:border-loop">
                        {condFields.map((f) => <option key={f} value={f}>{f}</option>)}
                      </select>
                      <select data-testid={`cond-op-${gi}-${ci}`} value={c.operator} onChange={(e) => updCond(gi, ci, { operator: e.target.value })} className="h-9 rounded-lg border border-sky bg-white px-2 text-xs outline-none focus:border-loop">
                        {condOps.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                      {!["is empty", "is not empty"].includes(c.operator) && (
                        <input data-testid={`cond-value-${gi}-${ci}`} value={c.value || ""} onChange={(e) => updCond(gi, ci, { value: e.target.value })} placeholder="value" className="h-9 w-28 rounded-lg border border-sky px-2 text-xs outline-none focus:border-loop" />
                      )}
                      <button type="button" onClick={() => removeCond(gi, ci)} className="rounded p-1 text-red-400 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addCond(gi)} data-testid={`add-condition-${gi}`} className="text-[11px] font-semibold text-loop hover:underline">+ Add Condition</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isFallback && <p className="rounded-2xl border-2 border-dashed border-sky bg-mist/50 p-4 text-sm text-ink/60">This is the <strong>fallback rule</strong> — applies when no other rule matches.</p>}

      {/* THEN block */}
      <div className="rounded-2xl border-2 border-dashed border-sky bg-mist/50 p-4">
        <p className="mb-2 font-sora text-xs font-bold uppercase tracking-wide text-loop">THEN assign using</p>
        <select data-testid="rule-assignment-type" value={form.assignment_type} onChange={(e) => set("assignment_type", e.target.value)} className="mb-3 h-10 w-full rounded-lg border border-sky bg-white px-2 text-sm outline-none focus:border-loop">
          {(options?.assignment_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <p className="mb-2 text-xs font-medium text-ink/60">Salespersons {isWeighted && <span className={weightTotal === 100 ? "text-emerald-600" : "text-red-500"}>· {weightTotal}%/100%</span>}</p>
        <div className="space-y-2">
          {exec.map((u) => {
            const selected = form.assigned_users.includes(u.id);
            return (
              <div key={u.id} className={`flex items-center gap-2 rounded-lg border p-2 ${selected ? "border-loop bg-sky/30" : "border-sky bg-white"}`}>
                <label className="flex flex-1 items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid={`rule-user-${u.id}`} checked={selected} onChange={() => toggleUser(u.id)} className="h-4 w-4 accent-loop" />
                  <span className="text-sm font-medium text-navy">{u.name}</span>
                  <span className={`text-[10px] ${u.status === "Active" ? "text-emerald-600" : "text-amber-600"}`}>{u.status}</span>
                </label>
                {selected && isWeighted && (
                  <div className="flex items-center gap-1">
                    <input type="number" min="0" max="100" data-testid={`rule-weight-${u.id}`} value={form.weights[u.id] ?? ""} onChange={(e) => set("weights", { ...form.weights, [u.id]: e.target.value })} className="h-8 w-16 rounded border border-sky px-2 text-sm outline-none focus:border-loop" />
                    <span className="text-xs text-ink/50">%</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="mb-1 block text-sm font-medium text-ink/80">Priority (lower wins)</label>
          <input type="number" data-testid="rule-priority" value={form.priority} onChange={(e) => set("priority", e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3.5 text-sm outline-none focus:border-loop" />
        </div>
        <div><label className="mb-1 block text-sm font-medium text-ink/80">Status</label>
          <select value={form.status} onChange={(e) => set("status", e.target.value)} className="h-11 w-full rounded-xl border border-sky px-3.5 text-sm outline-none focus:border-loop">
            <option value="Active">Active</option><option value="Inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={onClose} className="flex-1 rounded-xl border border-sky py-2.5 text-sm font-medium text-ink/70 hover:bg-mist">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="rule-save-button" className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-loop py-2.5 text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save rule"}
        </button>
      </div>
    </div>
  );
}

function RuleCard({ rule, users, onEdit, onToggle, onDelete }) {
  const weighted = rule.assignment_type === "Weighted Round Robin";
  const split = rule.assigned_user_names?.map((n, i) => {
    const id = rule.assigned_users[i];
    return weighted ? `${n} ${rule.weights?.[id] || 0}%` : n;
  }).join(" · ");
  return (
    <div data-testid={`rule-card-${rule.id}`} className={`rounded-2xl border bg-white p-5 transition-shadow hover:shadow-md ${rule.match_type === "fallback" ? "border-loop/40 bg-sky/10" : "border-sky/70"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {rule.match_type === "fallback" && <Shield className="h-4 w-4 text-loop" />}
          <h3 className="font-sora font-semibold text-navy">{rule.rule_name}</h3>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${rule.status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{rule.status}</span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        {rule.source_scope && rule.match_type !== "fallback" && (
          <span data-testid={`rule-scope-${rule.id}`} className="rounded-lg bg-loop/10 px-2.5 py-1 text-xs font-semibold text-loop">{SCOPE_BADGE[rule.source_scope] || rule.source_scope}</span>
        )}
        {rule.match_type !== "fallback" ? (
          rule.match_type && rule.match_value ? (
            <span className="rounded-lg bg-mist px-2.5 py-1 text-xs text-navy">{MATCH_LABELS[rule.match_type]}: <span className="font-mono">{rule.match_label || rule.match_value}</span></span>
          ) : rule.condition_groups?.length ? (
            <span className="rounded-lg bg-mist px-2.5 py-1 text-xs text-ink/70">{rule.condition_groups.length} OR group{rule.condition_groups.length > 1 ? "s" : ""}</span>
          ) : null
        ) : <span className="rounded-lg bg-mist px-2.5 py-1 text-xs text-ink/60">No match → fallback</span>}
        <ArrowRight className="h-4 w-4 text-ink/30" />
        <span className="rounded-lg bg-sky/40 px-2.5 py-1 text-xs font-medium text-navy">{rule.assignment_type}</span>
      </div>
      <p className="mt-2 text-sm text-ink/70"><span className="text-ink/50">Split:</span> {split || "—"}</p>
      <div className="mt-3 flex items-center justify-between border-t border-sky/50 pt-3">
        <span className="text-xs text-ink/40">Priority {rule.priority} · updated {fmtDate(rule.updated_at)}</span>
        <div className="flex gap-1">
          <button data-testid={`rule-toggle-${rule.id}`} onClick={() => onToggle(rule)} title="Toggle status" className="rounded-lg border border-sky p-1.5 text-ink/60 hover:bg-mist"><Power className="h-3.5 w-3.5" /></button>
          <button data-testid={`rule-edit-${rule.id}`} onClick={() => onEdit(rule)} className="rounded-lg border border-sky p-1.5 text-loop hover:bg-sky/30"><Pencil className="h-3.5 w-3.5" /></button>
          {rule.match_type !== "fallback" && <button data-testid={`rule-delete-${rule.id}`} onClick={() => onDelete(rule)} className="rounded-lg border border-sky p-1.5 text-red-500 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
      </div>
    </div>
  );
}

export default function Rules() {
  const { users } = useOutletContext();
  const { options, can, has } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null); // null | 'new' | ruleObj
  const [toDelete, setToDelete] = useState(null);
  const [testerOpen, setTesterOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data } = await api.get("/assignment-rules"); setData(data.data); }
    catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (rule) => {
    try {
      await api.put(`/assignment-rules/${rule.id}`, { ...rule, status: rule.status === "Active" ? "Inactive" : "Active" });
      toast.success("Status updated"); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => {
    await api.delete(`/assignment-rules/${toDelete.id}`);
    toast.success("Rule deleted"); load();
  };

  if (loading && !data) return <Loading label="Loading rules..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const fallback = data.rules.find((r) => r.match_type === "fallback");
  const normal = data.rules.filter((r) => r.match_type !== "fallback");
  const readOnly = !has("assignment_rules.create") && !has("assignment_rules.edit");

  return (
    <div className="space-y-6" data-testid="rules-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><GitBranch className="h-7 w-7 text-loop" />Assignment Rules</h1>
          <p className="text-sm text-ink/60">Route every inbound lead to the right salesperson automatically.</p>
        </div>
        {!readOnly && (
          <button onClick={() => setDialog("new")} data-testid="new-rule-button" className="flex items-center gap-2 rounded-xl bg-loop px-4 py-2.5 text-sm font-semibold text-white hover:bg-loophover">
            <Plus className="h-4 w-4" />New Rule
          </button>
        )}
        <button onClick={() => setTesterOpen(true)} data-testid="rule-tester-button" className="flex items-center gap-2 rounded-xl border border-sky bg-white px-4 py-2.5 text-sm font-semibold text-navy hover:bg-mist">
          <FlaskConical className="h-4 w-4 text-loop" />Test a lead
        </button>
      </div>

      {data.unassigned_count > 0 && (
        <div data-testid="unassigned-alert" className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <p className="text-sm text-amber-800"><strong>{data.unassigned_count}</strong> lead(s) could not be assigned and are waiting in Unassigned.</p>
          <a href="/leads?assigned=false" className="ml-auto text-sm font-semibold text-amber-700 hover:underline">View →</a>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {normal.map((r) => <RuleCard key={r.id} rule={r} users={users} onEdit={setDialog} onToggle={toggle} onDelete={setToDelete} />)}
      </div>

      <div>
        <h2 className="mb-3 font-sora text-sm font-semibold text-navy">Fallback Rule</h2>
        {fallback ? <div className="max-w-md"><RuleCard rule={fallback} users={users} onEdit={setDialog} onToggle={toggle} onDelete={setToDelete} /></div> :
          <p className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-700">No fallback rule. Leads with no match will go to Unassigned. {!readOnly && "Create one with match type 'Fallback'."}</p>}
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-sky bg-white sm:max-w-lg" data-testid="rule-dialog">
          <DialogHeader><DialogTitle className="font-sora text-navy">{dialog === "new" ? "New Assignment Rule" : "Edit Rule"}</DialogTitle></DialogHeader>
          <RuleForm rule={dialog !== "new" ? dialog : null} users={users} options={options} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(v) => !v && setToDelete(null)}
        onConfirm={remove}
        title="Delete assignment rule?"
        description={toDelete ? `"${toDelete.rule_name}" will be permanently deleted. Existing leads keep their owners.` : ""}
        confirmLabel="Delete rule"
        testid="delete-rule-dialog"
      />

      <RuleTester open={testerOpen} onOpenChange={setTesterOpen} options={options} />
    </div>
  );
}

function RuleTester({ open, onOpenChange, options }) {
  const [sample, setSample] = useState({ ingestion_source: "meta_n8n" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setSample((s) => ({ ...s, [k]: v }));

  const run = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(Object.entries(sample).filter(([, v]) => String(v || "").trim()));
      const { data } = await api.post("/assignment-rules/test", payload);
      setResult(data.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const FIELD = ({ k, label, type = "text" }) => (
    <div>
      <label className="mb-1 block text-[11px] font-medium text-ink/50">{label}</label>
      {type === "select" ? (
        <select data-testid={`tester-${k}`} value={sample[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-9 w-full rounded-lg border border-sky bg-white px-2 text-sm outline-none focus:border-loop">
          <option value="meta_n8n">Meta (meta_n8n)</option>
          <option value="manual">Manual</option>
          <option value="import">CSV import</option>
        </select>
      ) : (
        <input data-testid={`tester-${k}`} value={sample[k] || ""} onChange={(e) => set(k, e.target.value)} className="h-9 w-full rounded-lg border border-sky px-2 text-sm outline-none focus:border-loop" />
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-sky bg-white sm:max-w-lg" data-testid="rule-tester-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-sora text-navy"><FlaskConical className="h-5 w-5 text-loop" />Test a sample lead</DialogTitle></DialogHeader>
        <p className="text-xs text-ink/55">Dry-run the engine — see which rule wins, the source scope, and which OR group matched. Nothing is saved.</p>
        <div className="grid grid-cols-2 gap-2.5">
          <FIELD k="ingestion_source" label="Lead source" type="select" />
          <FIELD k="campaign_name" label="Campaign name" />
          <FIELD k="adset_name" label="Ad set name" />
          <FIELD k="ad_name" label="Ad name" />
          <FIELD k="form_name" label="Form name" />
          <FIELD k="service_interested" label="Service" />
          <FIELD k="budget_range" label="Budget" />
          <FIELD k="city" label="City" />
        </div>
        <button onClick={run} disabled={busy} data-testid="tester-run" className="flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-loop text-sm font-semibold text-white hover:bg-loophover disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run test"}
        </button>
        {result && (
          <div data-testid="tester-result" className={`rounded-xl border p-3 text-sm ${result.matched ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/50"}`}>
            <p className="font-semibold text-navy">{result.matched ? `Matched: ${result.rule?.rule_name}` : "No rule matched"}</p>
            <p className="mt-1 text-xs text-ink/60">Lead source: <span className="font-medium text-navy">{result.lead_source}</span></p>
            {result.matched && (
              <>
                <p className="text-xs text-ink/60">Source scope: <span className="font-medium text-navy">{result.matched_scope}</span></p>
                {result.matched_group_label && <p className="text-xs text-ink/60">Matched: <span className="font-medium text-navy">{result.matched_group_label}</span></p>}
                <p className="text-xs text-ink/60">Assign to: <span className="font-medium text-navy">{(result.rule?.assigned_user_names || []).join(", ") || "—"}</span> ({result.rule?.assignment_type})</p>
              </>
            )}
            <p className="mt-1 text-xs text-ink/50">{result.reason}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
